import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Location', href: '/location' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#020202]/97 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="px-8 lg:px-20 flex items-center justify-between h-[78px]">
        {/* Logo */}
        <Link to="/" className="flex flex-col leading-none cursor-pointer group flex-shrink-0">
          <span className="font-serif text-[1.1rem] font-bold text-white tracking-[0.15em] uppercase group-hover:text-gold transition-colors duration-300">IMMANUEL</span>
          <span className="text-gold text-[0.52rem] tracking-[0.38em] uppercase font-sans font-medium">Production Group</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`relative font-sans text-[0.65rem] tracking-[0.25em] uppercase transition-colors duration-300 cursor-pointer group whitespace-nowrap ${
                location.pathname === link.href ? 'text-gold' : 'text-white/50 hover:text-white'
              }`}
            >
              {link.label}
              <span
                className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-300 ${
                  location.pathname === link.href ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              />
            </Link>
          ))}
        </nav>

        {/* Sticky CTA */}
        <div className="hidden lg:flex items-center gap-5">
          <a
            href="tel:+14074000000"
            rel="nofollow"
            className="whitespace-nowrap font-sans text-[0.6rem] tracking-[0.2em] uppercase text-white/35 hover:text-gold transition-colors duration-300 cursor-pointer flex items-center gap-2"
          >
            <i className="ri-phone-line text-gold text-xs" />
            Click to Call
          </a>
          <Link
            to="/contact"
            className="whitespace-nowrap font-sans text-[0.62rem] font-semibold tracking-[0.22em] uppercase px-7 py-3.5 bg-gold text-[#020202] hover:bg-white transition-colors duration-300 cursor-pointer"
          >
            Get a Quote
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden text-white cursor-pointer p-2 hover:text-gold transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-5 flex flex-col gap-1.5">
            <span className={`h-px bg-current transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`h-px bg-current transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`h-px bg-current transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-[#020202]/99 backdrop-blur-2xl border-t border-white/5 px-8 pt-8 pb-10 flex flex-col gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-sans text-sm tracking-[0.22em] uppercase transition-colors cursor-pointer flex items-center gap-3 ${
                location.pathname === link.href ? 'text-gold' : 'text-white/60 hover:text-gold'
              }`}
            >
              {location.pathname === link.href && <div className="w-3 h-px bg-gold" />}
              {link.label}
            </Link>
          ))}
          <div className="mt-2 pt-6 border-t border-white/5 flex flex-col gap-3">
            <Link
              to="/contact"
              className="whitespace-nowrap w-fit font-sans text-[0.65rem] font-semibold tracking-[0.22em] uppercase px-8 py-4 bg-gold text-[#020202] cursor-pointer"
            >
              Get a Quote
            </Link>
            <a href="tel:+14074000000" rel="nofollow" className="font-sans text-xs text-white/35 flex items-center gap-2 cursor-pointer">
              <i className="ri-phone-line text-gold" /> Click to Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
